if (!customElements.get('eg-main-product')) {
  class EgMainProduct extends HTMLElement {
    constructor() {
      super();
      this.gallery = null;
      this.slides = [];
      this.dots = [];
      this.currentSlide = 0;
      this.totalSlides = 0;
      this.currentVariantId = null;
      this.sellingPlanId = null;
      this.productData = null;
    }

    connectedCallback() {
      this.productData = JSON.parse(this.querySelector('[data-product-json]')?.textContent || '{}');
      this.currentVariantId = this.productData.selected_or_first_available_variant?.id || this.productData.variants?.[0]?.id;

      this.initGallery();
      this.initVariants();
      this.initSubscription();
      this.initDisclosures();
      this.initATC();
    }

    /* ===== Gallery ===== */
    initGallery() {
      this.gallery = this.querySelector('[data-gallery-slides]');
      if (!this.gallery) return;

      this.slides = this.gallery.querySelectorAll('.eg-main-product__gallery-slide');
      this.dots = this.querySelectorAll('.eg-main-product__gallery-dot');
      this.totalSlides = this.slides.length;

      const prevBtn = this.querySelector('[data-gallery-prev]');
      const nextBtn = this.querySelector('[data-gallery-next]');

      if (prevBtn) prevBtn.addEventListener('click', () => this.goToSlide(this.currentSlide - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.goToSlide(this.currentSlide + 1));

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.goToSlide(i));
      });

      this.gallery.addEventListener('scroll', () => {
        const index = Math.round(this.gallery.scrollLeft / this.gallery.offsetWidth);
        if (index >= 0 && index < this.totalSlides) {
          this.updateGalleryControls(index);
        }
      }, { passive: true });

      this.updateGalleryControls(0);
    }

    goToSlide(index) {
      if (index < 0 || index >= this.totalSlides) return;
      this.gallery.scrollTo({ left: index * this.gallery.offsetWidth });
    }

    updateGalleryControls(index) {
      this.currentSlide = index;
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));

      const prevBtn = this.querySelector('[data-gallery-prev]');
      const nextBtn = this.querySelector('[data-gallery-next]');
      if (prevBtn) prevBtn.disabled = index === 0;
      if (nextBtn) nextBtn.disabled = index === this.totalSlides - 1;
    }

    /* ===== Variants ===== */
    initVariants() {
      const inputs = this.querySelectorAll('[data-variant-input]');
      inputs.forEach(input => {
        input.addEventListener('change', () => this.onVariantChange());
      });
    }

    onVariantChange() {
      const selectedOptions = {};
      this.querySelectorAll('[data-variant-input]:checked').forEach(input => {
        selectedOptions[input.dataset.optionName] = input.value;
      });

      const variant = this.productData.variants?.find(v => {
        return v.options.every((opt, i) => {
          const optionName = this.productData.options[i];
          return opt === selectedOptions[optionName];
        });
      });

      if (variant) {
        this.currentVariantId = variant.id;
        this.updateATCState(variant);
        this.updateURL(variant);

        if (this.querySelector('[data-purchase-options]')) {
          this.updateSubscriptionPrices();
        } else {
          this.updatePrice(variant);
        }
      }
    }

    formatMoney(cents) {
      const fmt = this.productData.money_format || '€{{amount}}';
      const value = cents / 100;
      const amountFormats = {
        'amount': value.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,'),
        'amount_no_decimals': Math.floor(value).toString().replace(/(\d)(?=(\d{3})+$)/g, '$1,'),
        'amount_with_comma_separator': value.toFixed(2).replace('.', ',').replace(/(\d)(?=(\d{3})+,)/g, '$1.'),
        'amount_no_decimals_with_comma_separator': Math.floor(value).toString().replace(/(\d)(?=(\d{3})+$)/g, '$1.'),
        'amount_with_apostrophe_separator': value.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1'")
      };
      let result = fmt;
      for (const [key, val] of Object.entries(amountFormats)) {
        result = result.replace(new RegExp('\\{\\{\\s*' + key + '\\s*\\}\\}'), val);
      }
      return result;
    }

    updatePrice(variant) {
      const priceEl = this.querySelector('[data-current-price]');
      const compareEl = this.querySelector('[data-compare-price]');
      if (!priceEl) return;

      priceEl.innerHTML = this.formatMoney(variant.price);

      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.innerHTML = this.formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
    }

    updateATCState(variant) {
      const btn = this.querySelector('[data-atc-button]');
      if (!btn) return;
      btn.disabled = !variant.available;
    }

    updateURL(variant) {
      if (!variant) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    /* ===== Subscription ===== */
    initSubscription() {
      const purchaseOptions = this.querySelector('[data-purchase-options]');
      if (!purchaseOptions) return;

      const firstPlanInput = this.querySelector('[data-selling-plan-input]:checked');
      if (firstPlanInput) {
        this.sellingPlanId = parseInt(firstPlanInput.value);
      }

      this.querySelectorAll('[data-purchase-type]').forEach(input => {
        input.addEventListener('change', () => this.onPurchaseTypeChange(input.value));
      });

      this.querySelectorAll('[data-selling-plan-input]').forEach(input => {
        input.addEventListener('change', () => this.onSellingPlanChange(parseInt(input.value)));
      });

      this.updateSubscriptionPrices();
    }

    onPurchaseTypeChange(type) {
      const subscribeBlock = this.querySelector('[data-purchase-option="subscribe"]');
      const onetimeBlock = this.querySelector('[data-purchase-option="onetime"]');

      if (type === 'subscribe') {
        const checkedPlan = this.querySelector('[data-selling-plan-input]:checked');
        this.sellingPlanId = checkedPlan ? parseInt(checkedPlan.value) : null;
      } else {
        this.sellingPlanId = null;
      }

      this.updateSubscriptionPrices();
    }

    onSellingPlanChange(planId) {
      this.sellingPlanId = parseInt(planId);
      this.updateSubscriptionPrices();
    }

    updateSubscriptionPrices() {
      const variant = this.productData.variants?.find(v => v.id === this.currentVariantId);
      if (!variant) return;

      const allocations = variant.selling_plan_allocations || [];
      const subscribePriceEl = this.querySelector('[data-subscribe-price]');
      const subscribeCompareEl = this.querySelector('[data-subscribe-compare]');
      const onetimePriceEl = this.querySelector('[data-onetime-price]');

      const checkedPlan = this.querySelector('[data-selling-plan-input]:checked');
      const selectedPlanId = checkedPlan ? Number(checkedPlan.value) : null;
      const alloc = selectedPlanId ? allocations.find(a => a.selling_plan_id === selectedPlanId) : null;

      if (subscribePriceEl && alloc) {
        subscribePriceEl.innerHTML = this.formatMoney(alloc.price);
      }

      if (subscribeCompareEl) {
        subscribeCompareEl.innerHTML = this.formatMoney(variant.price);
      }

      if (onetimePriceEl) {
        onetimePriceEl.innerHTML = this.formatMoney(variant.price);
      }

      const isSubscribe = this.querySelector('[data-purchase-type]:checked')?.value === 'subscribe';
      if (isSubscribe && alloc) {
        this.updatePrice({
          price: alloc.price,
          compare_at_price: variant.price > alloc.price ? variant.price : null
        });
      } else {
        this.updatePrice(variant);
      }
    }

    /* ===== Disclosures ===== */
    initDisclosures() {
      this.querySelectorAll('.eg-main-product__disclosure').forEach(disclosure => {
        const content = disclosure.querySelector('.eg-main-product__disclosure-content');
        if (disclosure.classList.contains('is-open') && content) {
          content.style.maxHeight = content.scrollHeight + 'px';
        }
      });

      this.querySelectorAll('[data-disclosure-trigger]').forEach(trigger => {
        trigger.addEventListener('click', () => {
          const disclosure = trigger.closest('.eg-main-product__disclosure');
          if (!disclosure) return;
          const content = disclosure.querySelector('.eg-main-product__disclosure-content');
          if (!content) return;

          if (disclosure.classList.contains('is-open')) {
            content.style.maxHeight = content.scrollHeight + 'px';
            requestAnimationFrame(() => {
              content.style.maxHeight = '0px';
            });
            disclosure.classList.remove('is-open');
          } else {
            disclosure.classList.add('is-open');
            content.style.maxHeight = content.scrollHeight + 'px';
            content.addEventListener('transitionend', () => {
              if (disclosure.classList.contains('is-open')) {
                content.style.maxHeight = 'none';
              }
            }, { once: true });
          }
        });
      });
    }

    /* ===== Add to Cart ===== */
    initATC() {
      const btn = this.querySelector('[data-atc-button]');
      if (!btn) return;

      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        if (!this.currentVariantId || btn.disabled) return;

        btn.classList.add('is-loading');

        try {
          await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });

          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: this.currentVariantId,
                quantity: 1,
                ...(this.sellingPlanId ? { selling_plan: this.sellingPlanId } : {})
              }]
            })
          });

          if (!res.ok) throw new Error('Add to cart failed');

          window.location.href = '/checkout';
        } catch (err) {
          console.error('EG Main Product ATC Error:', err);
          btn.classList.remove('is-loading');
        }
      });
    }
  }

  customElements.define('eg-main-product', EgMainProduct);
}
